import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthenticationResponseDto } from '@fleet-beat/auth-api';
import { AuthHeaders, UserDto } from '@fleet-beat/core';
import { BehaviorSubject } from 'rxjs';
import { AuthData } from '../models';
import { TenantAuthResponseDto } from '@fleet-beat/api';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  get authenticated(): boolean {
    return Boolean(this.authDataSubject.value?.token && localStorage.getItem(this.tokenKey));
  }

  get tenantSelected(): boolean {
    return Boolean(this.authDataSubject.value?.refreshToken && localStorage.getItem(this.refreshTokenKey));
  }

  get hasAdminAuthenticated(): boolean {
    return Boolean(localStorage.getItem(this.adminKey));
  }

  get authHeaders(): AuthHeaders {
    return {
      token: this.authDataSubject.value?.token,
      refreshToken: this.authDataSubject.value?.refreshToken,
    };
  }

  get authUser(): UserDto {
    return JSON.parse(this.authDataSubject.value?.user);
  }

  private readonly tokenKey: string = 'fb-token';
  private readonly refreshTokenKey: string = 'fb-refreshToken';
  private readonly adminKey: string = 'fb-admin';
  private readonly userKey: string = 'fb-user';

  private authDataSubject: BehaviorSubject<AuthData> = new BehaviorSubject(undefined);

  constructor(private router: Router) {
    this.readAuthData();
  }

  handleAuthenticationResponse(response: AuthenticationResponseDto): void {
    this.saveAuthData({
      token: response.token,
      refreshToken: response?.refreshToken,
      user: JSON.stringify(response.user),
    });
  }

  handleCompanyAuthorizationAndReplaceWithUser(response: TenantAuthResponseDto, companyId: string): void {
    const data: AuthData = this.authDataSubject.value; // NOTE data of authorized ADMIN user
    this.saveAdminAuthData(data);
    this.saveAuthData({
      token: response.token,
      user: JSON.stringify(response.user),
    });
    this.router.navigate(['/app/company', companyId]).then(() => {
      window.location.reload();
    });
  }

  replaceUserWithAdmin(url: string = '/admin/companies'): void {
    const data: AuthData = this.readAdminAuthData(); // NOTE data of authorized USER
    this.saveAuthData({
      token: data.token,
      user: data.user,
    });
    this.saveAdminAuthData(null);
    this.router.navigate([url]).then(() => {
      window.location.reload();
    });
  }

  signOut(): void {
    const user: UserDto = JSON.parse(localStorage.getItem(this.userKey));
    if (user) {
      localStorage.setItem('fb-userTypeLogOut', user.role);
    }
    this.saveAuthData(null);
    this.saveAdminAuthData(null);
    location.reload();
  }

  readAuthData(): void {
    const data: AuthData = {
      token: localStorage.getItem(this.tokenKey),
      refreshToken: localStorage.getItem(this.refreshTokenKey),
      user: localStorage.getItem(this.userKey),
    };
    this.authDataSubject.next(data);
  }

  private saveAdminAuthData(data: AuthData): void {
    if (data) {
      localStorage.setItem(this.adminKey, JSON.stringify(data));
    } else {
      localStorage.removeItem(this.adminKey);
    }
  }

  private readAdminAuthData(): AuthData {
    if (localStorage.getItem(this.adminKey)) {
      return JSON.parse(localStorage.getItem(this.adminKey));
    } else {
      return null;
    }
  }

  private saveAuthData(data: AuthData): void {
    this.authDataSubject.next(data);
    if (data?.token) {
      localStorage.setItem(this.tokenKey, data.token);
    } else {
      localStorage.removeItem(this.tokenKey);
    }

    if (data?.refreshToken) {
      localStorage.setItem(this.refreshTokenKey, data.refreshToken);
    } else {
      localStorage.removeItem(this.refreshTokenKey);
    }

    if (data?.user) {
      localStorage.setItem(this.userKey, data.user);
    } else {
      localStorage.removeItem(this.userKey);
    }
  }
}
